import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Home, ChevronRight, Save, X, Loader2, Plane } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const CARRIER_TYPES = [
  { value: 'AIRLINE', label: 'Airline' },
  { value: 'SHIPPING_LINE', label: 'Shipping Line' },
  { value: 'COURIER', label: 'Courier' },
];

const EMPTY_FORM = {
  carrier_name: '', carrier_type: '', carrier_prefix_code: '', carrier_code: '',
  country: '', contact_person: '', phone: '', email: '', website: '',
  remarks: '', is_active: true
};

const AddCarrier = () => {
  const navigate = useNavigate();
  const { id: carrierId } = useParams();
  const { toast } = useToast();
  const isEditMode = !!carrierId;

  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    const fetchCarrier = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${backendUrl}/api/carriers/${carrierId}`, { headers: getAuthHeaders() });
        if (!response.ok) throw new Error('Failed to load carrier');
        const data = await response.json();
        setFormData({
          carrier_name: data.carrier_name || '', carrier_type: data.carrier_type || '',
          carrier_prefix_code: data.carrier_prefix_code || '', carrier_code: data.carrier_code || '',
          country: data.country || '', contact_person: data.contact_person || '',
          phone: data.phone || '', email: data.email || '', website: data.website || '',
          remarks: data.remarks || '', is_active: data.is_active ?? true
        });
      } catch (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        navigate('/settings/carriers');
      } finally { setLoading(false); }
    };
    fetchCarrier();
  }, [isEditMode, carrierId, backendUrl, getAuthHeaders, toast, navigate]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!formData.carrier_name.trim()) { toast({ title: "Error", description: "Carrier name is required", variant: "destructive" }); return; }
    if (!formData.carrier_type) { toast({ title: "Error", description: "Carrier type is required", variant: "destructive" }); return; }
    try {
      setSaving(true);
      const url = isEditMode ? `${backendUrl}/api/carriers/${carrierId}` : `${backendUrl}/api/carriers`;
      const response = await fetch(url, { method: isEditMode ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(formData) });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save carrier');
      }
      toast({ title: "Success", description: isEditMode ? "Carrier updated" : "Carrier created" });
      navigate('/carriers');
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-accent-blue" /></div>;

  return (
    <div className="space-y-6 pb-8" data-testid="add-carrier-page">
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Home className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/')}>Home</span>
        <ChevronRight className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/settings')}>Settings</span>
        <ChevronRight className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/settings/carriers')}>Carrier Master</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-text-dark font-medium">{isEditMode ? 'Edit Carrier' : 'Add Carrier'}</span>
      </div>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-dark font-manrope">{isEditMode ? 'Edit Carrier' : 'Add Carrier'}</h1>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/carriers')} data-testid="cancel-btn"><X className="w-4 h-4 mr-2" /> Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-carrier-btn" className="bg-accent-blue hover:bg-accent-blue/90">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Carrier'}
          </Button>
        </div>
      </div>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-sky-500 px-6 py-4">
          <div className="flex items-center gap-3">
            <Plane className="w-5 h-5 text-sky-500" />
            <h2 className="text-lg font-semibold text-text-dark font-manrope">CARRIER DETAILS</h2>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Carrier Name <span className="text-red-500">*</span></Label>
              <Input data-testid="carrier-name-input" value={formData.carrier_name} onChange={e => handleChange('carrier_name', e.target.value)} placeholder="e.g., Emirates SkyCargo" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Carrier Type <span className="text-red-500">*</span></Label>
              <Select value={formData.carrier_type || "none"} onValueChange={v => handleChange('carrier_type', v === "none" ? '' : v)}>
                <SelectTrigger data-testid="carrier-type-select" className="border-gray-200"><SelectValue placeholder="Select Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select Type --</SelectItem>
                  {CARRIER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Prefix Code</Label>
              <Input data-testid="prefix-code-input" value={formData.carrier_prefix_code} onChange={e => handleChange('carrier_prefix_code', e.target.value)} placeholder="e.g., 176" className="border-gray-200 font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Carrier Code</Label>
              <Input data-testid="carrier-code-input" value={formData.carrier_code} onChange={e => handleChange('carrier_code', e.target.value.toUpperCase())} placeholder="e.g., EK" className="border-gray-200 font-mono" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Country</Label>
              <Input data-testid="country-input" value={formData.country} onChange={e => handleChange('country', e.target.value)} placeholder="e.g., UAE" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Contact Person</Label>
              <Input data-testid="contact-person-input" value={formData.contact_person} onChange={e => handleChange('contact_person', e.target.value)} placeholder="Contact name" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Phone</Label>
              <Input data-testid="phone-input" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="+91 98765 43210" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Email</Label>
              <Input data-testid="email-input" type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} placeholder="carrier@example.com" className="border-gray-200" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Website</Label>
              <Input data-testid="website-input" value={formData.website} onChange={e => handleChange('website', e.target.value)} placeholder="https://..." className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Remarks</Label>
              <Textarea data-testid="remarks-input" value={formData.remarks} onChange={e => handleChange('remarks', e.target.value)} placeholder="Optional notes..." className="border-gray-200 min-h-[80px]" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch data-testid="active-toggle" checked={formData.is_active} onCheckedChange={v => handleChange('is_active', v)} />
            <Label className="text-sm font-ibm">{formData.is_active ? 'Active' : 'Inactive'}</Label>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AddCarrier;
